import * as React from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../lib/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

// Extracted so a non-Radix-Select component can render a trigger that looks identical to this one.
// min-w-0 clears the automatic min-width a grid/flex item defaults to its content, letting a long
// value grow past its column; truncate replaces line-clamp because a flex item blockifies -webkit-box.
export const selectTriggerClassName = cn(
    'flex h-10 w-full min-w-0 items-center justify-between whitespace-nowrap',
    'cursor-pointer rounded-md border border-input bg-transparent px-3 py-2',
    'text-md ring-offset-background transition-all',
    'data-[placeholder]:text-muted-foreground [&>span]:min-w-0 [&>span]:truncate',
    'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/30',
    'focus:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-accent/75',
    'disabled:cursor-not-allowed disabled:opacity-50',
);

const selectTriggerVariants = cva(selectTriggerClassName, {
    variants: {
        variant: {
            default: '',
            ghost: 'h-9 gap-2 border-0 px-4 text-sm font-medium',
        },
    },
    defaultVariants: { variant: 'default' },
});

const SelectTrigger = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
        VariantProps<typeof selectTriggerVariants>
>(({ className, children, asChild, variant, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        asChild={asChild}
        tabIndex={0} // required for tabulation on new safari versions to work
        className={cn(selectTriggerVariants({ variant }), className)}
        {...props}
    >
        {asChild ? (
            children
        ) : (
            <>
                {children}
                <SelectPrimitive.Icon asChild>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </SelectPrimitive.Icon>
            </>
        )}
    </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={cn('flex cursor-default items-center justify-center py-1', className)}
        {...props}
    >
        <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={cn('flex cursor-default items-center justify-center py-1', className)}
        {...props}
    >
        <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content
            ref={ref}
            className={cn(
                'relative z-50 rounded-md border bg-popover text-popover-foreground shadow-md',
                'overflow-y-auto overflow-x-hidden',
                'max-h-[min(21rem,var(--radix-select-content-available-height))]',
                // Without a cap the menu sizes to its widest item and runs off a phone screen; taking
                // the width Radix measured is also what makes long labels wrap.
                'min-w-[8rem] max-w-(--radix-select-content-available-width)',
                'origin-(--radix-select-content-transform-origin)',
                'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
                'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
                position === 'popper' &&
                    'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                className,
            )}
            position={position}
            {...props}
        >
            <SelectScrollUpButton />
            <SelectPrimitive.Viewport
                // No height class: Radix's inline flex-basis on this element overrides height, so even an inline height is ignored.
                className={cn(
                    'p-1',
                    position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]',
                )}
            >
                {children}
            </SelectPrimitive.Viewport>
            <SelectScrollDownButton />
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label
        ref={ref}
        className={cn('px-2 py-1.5 text-sm font-medium text-muted-foreground', className)}
        {...props}
    />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { indicatorClassName?: string }
>(({ className, children, indicatorClassName, ...props }, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={cn(
            'relative flex w-full items-center rounded-md py-2.5 pl-2 pr-8',
            'cursor-default select-none text-base leading-5 outline-none',
            'data-[state=unchecked]:focus:bg-accent data-[state=unchecked]:focus:text-accent-foreground',
            'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            className,
        )}
        {...props}
    >
        <span
            className={cn(
                'absolute right-2 flex h-3.5 w-3.5 items-center justify-center',
                indicatorClassName,
            )}
        >
            <SelectPrimitive.ItemIndicator>
                <Check className="h-4 w-4" />
            </SelectPrimitive.ItemIndicator>
        </span>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
    React.ComponentRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={cn('-mx-1 my-1 h-px bg-muted', className)}
        {...props}
    />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
};
